import type { ProductApi } from './types';

export function useProductApi(): ProductApi {
  /**
   * Get details of a product by its identifier
   * @param productIdentifier - The product's identifier (SKU, ID, etc.)
   * @param productIdType - The type of identifier (SKU, ID, etc.)
   * @param locale - The locale in which the product is fetched
   */
  const getProductbyId = async ({
    productIdentifier,
    productIdType = 'EXTERNAL_ID',
    locale,
  }: GetProductRequest): Promise<GetProductResponse> => {
    try {
      const { data, error } = await useFetch<GetProductResponse>(
        `/api/product/${productIdentifier}`,
        {
          params: {
            productIdType,
            locale,
          },
          method: 'GET',
        }
      );

      if (error.value) {
        console.error('Failed to fetch product:', error.value);
        return {} as GetProductResponse;
      }
      return data.value as GetProductResponse;
    } catch (error) {
      console.error('An unexpected error occurred:', error);
      return {} as GetProductResponse;
    }
  };
  /**
   * Get related products of a product by sku, id or externalId
   * @param productIdentifier - The product's identifier (SKU, ID, etc.)
   * @param productIdType - The type of identifier (SKU, ID, etc.)
   * @param locale - The locale in which the related products are fetched
   * @param currency - The currency in which the related products are fetched
   * @param pageable - Pagination options
   * @param {boolean} [withAggregation=false] - Whether to include aggregation data in the response.
   */
  const getRelatedProducts = async ({
    productIdentifier,
    productIdType,
    locale,
    currency,
    pageable,
    withAggregation = false, // Default to false if not provided
  }: GetRelatedProductsRequest & {
    withAggregation?: boolean;
  }): Promise<GetRelatedProductsResponse | null> => {
    try {
      const { data, error } = await useFetch<GetRelatedProductsResponse>(
        `/api/product/${productIdentifier}/related-products`,
        {
          method: 'GET',
          params: {
            productIdType,
            locale,
            currency,
            page: pageable?.page,
            size: pageable?.size,
            withAggregation, // Include withAggregation as a query parameter
          },
        }
      );

      if (error.value) {
        console.error('Failed to fetch related products:', error.value);
        return null;
      }

      return data.value || null;
    } catch (err) {
      console.error('Unexpected error while fetching related products:', err);
      return null;
    }
  };
  /**
   * Get paginated offers of a product by sku, id or externalId
   * @param productIdentifier - The product's identifier (SKU, ID, etc.)
   * @param productIdType - The type of identifier (SKU, ID, etc.)
   * @param currency - The currency in which the offers are fetched
   * @param locale - The locale in which the offers are fetched
   * @param withoutPrices - Whether to fetch offers without prices
   * @param pageable - Pagination options
   */
  const getProductPaginatedOffers = async ({
    productIdentifier,
    productIdType,
    currency,
    locale,
    withoutPrices = false,
    pageable,
  }: GetProductOffersRequest): Promise<GetProductOffersResponse | null> => {
    try {
      const { data, error } = await useFetch<GetProductOffersResponse>(
        `/api/product/${productIdentifier}/offers`,
        {
          method: 'GET',
          params: {
            productIdType,
            currency,
            locale,
            withoutPrices,
            page: pageable?.page,
            size: pageable?.size,
          },
        }
      );

      if (error.value) {
        console.error('Failed to fetch product paginated offers:', error.value);
        return null;
      }

      return data.value || null;
    } catch (err) {
      console.error('Unexpected error while fetching product paginated offers:', err);
      return null;
    }
  };
  /**
   * Get reviews of a product
   * @param productIdentifier - The product's identifier (SKU, ID, etc.)
   * @param productIdType - The type of identifier (SKU, ID, etc.)
   */
  const getProductReviews = async ({
    productIdentifier,
    productIdType,
  }: GetReviewRequest): Promise<GetProductReviewResponse | null> => {
    try {
      const { data, error } = await useFetch<GetProductReviewResponse>(
        `/api/product/${productIdentifier}/reviews`,
        {
          method: 'GET',
          params: {
            productIdentifier,
            productIdType,
          },
        }
      );
      if (error.value) {
        console.error('Failed to fetch product review:', error.value);
        return null;
      }

      return data.value || null;
    } catch (err) {
      console.error('Unexpected error while fetching product review:', err);
      return null;
    }
  };
  /**
   * Create a review for a product
   * @param message - The review message
   * @param productSku - The SKU of the product being reviewed
   * @param rating - The rating given by the user
   */
  const createProductReview = async ({
    message,
    productSku,
    rating,
  }: CreateReviewRequest): Promise<GetProductReviewResponse | null> => {
    try {
      const { data, error } = await useFetch<GetProductReviewResponse>(`/api/reviews`, {
        method: 'POST',
        body: {
          message,
          productSku,
          rating,
        },
      });
      if (error.value) {
        console.error('Failed to fetch product paginated offers:', error.value);
        return null;
      }

      return data.value || null;
    } catch (err) {
      console.error('Unexpected error while fetching product paginated offers:', err);
      return null;
    }
  };
  /**
   * Update a review for a product
   * @param productReviewId - The ID of the product review to update
   * @param message - The new review message
   * @param rating - The new rating
   */
  const updateProductReview = async ({
    productReviewId,
    message,
    rating,
  }: UpdateReviewRequest): Promise<PutProductReviewResponse | null> => {
    try {
      const { data, error } = await useFetch<PutProductReviewResponse>(
        `/api/reviews/${productReviewId}`,
        {
          method: 'PUT',
          params: {
            productReviewId,
            message,
            rating,
          },
        }
      );
      if (error.value) {
        console.error('Failed to fetch product paginated offers:', error.value);
        return null;
      }

      return data.value || null;
    } catch (err) {
      console.error('Unexpected error while fetching product paginated offers:', err);
      return null;
    }
  };
  const getProductVariants = async ({
    productSku,
    locale,
    pageable,
  }: GetProductVariantsRequest): Promise<GetProductVariantsResponse | null> => {
    try {
      const { data, error } = await useFetch<GetProductVariantsResponse>(
        `/api/product-variants/${productSku}`,
        {
          method: 'GET',
          params: {
            locale,
            page: pageable?.page,
            size: pageable?.size,
          },
        }
      );

      if (error.value) {
        console.error('Failed to fetch product variants:', error.value);
        return null;
      }

      return data.value || null;
    } catch (err) {
      console.error('Unexpected error while fetching product variants:', err);
      return null;
    }
  };
  const getProductVariantOffers = async ({
    productIdentifier,
    productIdType,
    currency,
  }: GetProductVariantOffersRequest): Promise<GetProductVariantOffersResponse | null> => {
    try {
      const { data, error } = await useFetch<GetProductVariantOffersResponse>(
        `/api/product-variants/${productIdentifier}/offers`,
        {
          method: 'GET',
          params: {
            productIdType,
            currency,
          },
        }
      );

      if (error.value) {
        console.error('Failed to fetch product variant offers:', error.value);
        return null;
      }

      return data.value || null;
    } catch (err) {
      console.error('Unexpected error while fetching product variant offers:', err);
      return null;
    }
  };
  return {
    getProductbyId,
    getRelatedProducts,
    getProductVariantOffers,
    getProductPaginatedOffers,
    createProductReview,
    getProductReviews,
    getProductVariants,
    updateProductReview,
  };
}
